import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import { FC, useEffect, useState } from 'react';
import {
	Box,
	DialogContent,
	LinearProgress,
	Step,
	StepLabel,
	Stepper,
	Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { CircleSvg } from 'ui/CircleSvg';
import { useParams } from 'react-router';
import { getUserInfo } from 'redux/reducers/user/selectors';
import { getErrorCorrection, getScripts } from 'redux/reducers/errorCorrections/selectors';
import { getScriptsRequest, startScriptRequest } from 'redux/reducers/errorCorrections/reducer';
import { COLORS } from 'services/constants/colors';
import { IErrorCorrectionsScriptParameterValue, TLaunchCorrectionScriptDialogProps } from './types';
import { Step1 } from './Step1';
import { Step2 } from './Step2';
import { Step3 } from './Step3';

const steps = ['Select Script', 'Additional parameters', 'Submit'];

export const LaunchCorrectionScriptDialog: FC<TLaunchCorrectionScriptDialogProps> = ({
	open,
	setClose,
	activeStep,
	setActiveStep,
	setErrorParams,
	errorParams,
	setSelectedScript,
	selectedScript,
	handleSubmit,
	setSubmitted,
	submitted,
	isGroupScripts = false,
}) => {
	// const params = useParams<{ errorId?: string }>();
	// const { errorId } = params;
	const dispatch = useDispatch();
	const scriptSubmitIsLoading = false;
	const scriptSubmitError = '';
	const user = useSelector(getUserInfo);
	const scripts = useSelector(getScripts);
	const errorItem = useSelector(getErrorCorrection);

	// const [activeStep, setActiveStep] = useState(0);
	// const [submitted, setSubmitted] = useState(false);
	// const [selectedScript, setSelectedScript] = useState<number | null>(null);
	// const [errorParams, setErrorParams] = useState<IErrorCorrectionsScriptParameterValue[]>(
	// 	scripts?.[selectedScript ?? 0]?.parameters.map((param) => {
	// 		return {
	// 			fieldName: param.fieldName,
	// 			name: param.name,
	// 			value: '',
	// 			type: param.type,
	// 		};
	// 	}) ?? [],
	// );

	useEffect(() => {
		const errorParamsTmp = [...errorParams];
		setErrorParams(errorParamsTmp);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [errorItem, user]);

	const handleScriptParametersChange = (index: number, value: string | boolean) => {
		setErrorParams((prev) => {
			const newParams = [...prev];
			newParams[index].value = value;
			return newParams;
		});
	};

	useEffect(
		() => {
			setErrorParams(
				scripts?.[selectedScript ?? 0]?.parameters.map((param) => {
					return {
						name: param.name,
						fieldName: param.fieldName,
						value: '',
						type: param.type,
					};
				}) ?? [],
			);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[selectedScript],
	);

	const handleEraseForm = () => {
		setSelectedScript(null);
		setErrorParams([]);
		setActiveStep(0);
	};

	const handleScriptChange = (index: number) => {
		handleEraseForm();
		setSelectedScript(index);
	};

	const handleClose = () => {
		setClose();
		setActiveStep(0);
		handleEraseForm();
	};

	const handleNext = () => {
		if (activeStep === 0 && scripts?.[selectedScript ?? 0]?.parameters.length === 0)
			setActiveStep(2);
		else setActiveStep((prevActiveStep) => prevActiveStep + 1);
	};

	const handleBack = () => {
		if (activeStep === 2 && scripts?.[selectedScript ?? 0]?.parameters.length === 0)
			setActiveStep(0);
		else setActiveStep((prevActiveStep) => prevActiveStep - 1);
		setSubmitted(false);
	};

	useEffect(() => {
		if (submitted && !scriptSubmitIsLoading && !scriptSubmitError) {
			setClose();
			setActiveStep(0);
			handleEraseForm();
			setSubmitted(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [scriptSubmitIsLoading]);

	// const handleSubmit = () => {
	// 	if (selectedScript === null || scripts === null) return;
	// 	dispatch(
	// 		startScriptRequest({
	// 			errorId: +errorId!,
	// 			scriptId: scripts[selectedScript].id,
	// 			parameters:
	// 				// {},
	// 				errorParams.reduce<{ [key: string]: any }>((acc, item) => {
	// 					acc[item.fieldName] = String(item.value);
	// 					return acc;
	// 				}, {}),
	// 		}),
	// 	);
	// 	setSubmitted(true);
	// 	handleEraseForm();
	// 	setClose();
	// };

	useEffect(
		() => {
			dispatch(getScriptsRequest());
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	return (
		<Dialog
			open={open}
			// onClose={setClose}
			aria-labelledby="alert-dialog-title"
			aria-describedby="alert-dialog-description"
			sx={{
				'.MuiDialog-paper': {
					width: '100%',
					maxWidth: '830px',
					minHeight: '500px',
					borderRadius: '12px',
					margin: { xs: 0, sm: 'auto' },
				},
			}}
		>
			<DialogTitle
				color="primary"
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
				}}
			>
				<span>Launch correction script</span>
				{selectedScript !== null && activeStep === 1 && (
					<Typography
						sx={{
							fontWeight: 'bold',
							fontSize: '1rem',
							backgroundColor: 'primary.light',
							borderRadius: '20px',
							padding: '5px 10px',
						}}
					>
						{scripts?.[selectedScript].name}
					</Typography>
				)}
			</DialogTitle>
			<DialogContent>
				<>
					<Box
						sx={{
							margin: '10px 0 20px 0',
							p: 0,
							display: { xs: 'none', sm: 'flex' },
							// width: '800px',
							// overflowX: 'hidden',
						}}
					>
						<Stepper
							activeStep={activeStep}
							sx={{
								p: 0,
								m: 0,
								// display: { xs: 'none', sm: 'flex' },
								// width: '800px',
								// overflowX: 'hidden',
							}}
						>
							{steps.map((label, index) => {
								const stepProps: { completed?: boolean } = {};
								const labelProps: {
									optional?: React.ReactNode;
								} = {};
								return (
									<Step key={label} {...stepProps}>
										<StepLabel {...labelProps}>{label}</StepLabel>
									</Step>
								);
							})}
						</Stepper>
					</Box>
					<Box sx={{ display: { sm: 'none', xs: 'flex' }, gap: 1, m: '8px 0px 16px 0px' }}>
						<CircleSvg num={activeStep + 1} active />{' '}
						<Typography
							sx={{
								color: COLORS.font_main,
								fontWeight: '500',
								fontSize: '0.875rem',
							}}
						>
							{steps[activeStep]}
						</Typography>
					</Box>
					{activeStep === 0 && (
						<Step1 selectedScript={selectedScript} handleScriptChange={handleScriptChange} />
					)}
					{activeStep === 1 && selectedScript !== null && (
						<Step2
							selectedScript={selectedScript}
							errorParams={errorParams}
							handleScriptParametersChange={handleScriptParametersChange}
						/>
					)}
					{activeStep === 2 && selectedScript !== null && (
						<Step3 selectedScript={selectedScript} errorParams={errorParams} />
					)}
					{scriptSubmitIsLoading && (
						<Box sx={{ width: '100%', mt: '10px' }}>
							<LinearProgress />
						</Box>
					)}
					{submitted && !scriptSubmitIsLoading && scriptSubmitError && (
						<Box sx={{ mt: '10px', color: 'red' }}>{scriptSubmitError}</Box>
					)}
				</>
			</DialogContent>

			<DialogActions>
				<Button
					onClick={() => {
						setSubmitted(false);
						handleClose();
					}}
					disabled={scriptSubmitIsLoading}
				>
					Cancel
				</Button>
				{activeStep !== 0 && activeStep < steps.length && (
					<Button onClick={handleBack} disabled={scriptSubmitIsLoading}>
						Back
					</Button>
				)}
				{activeStep === steps.length - 1 ? (
					<Button onClick={handleSubmit} disabled={scriptSubmitIsLoading || submitted}>
						{isGroupScripts ? 'Apply' : 'Launch'}
					</Button>
				) : (
					<Button
						onClick={handleNext}
						autoFocus
						disabled={
							selectedScript === null && activeStep === 0
							// ||
							// (activeStep === 2 && (!APIKey || !keyID || !storeID || !storeAddress)) ||
						}
					>
						Next
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
};
